import { Flex, MenuProps } from 'antd';
import { CheckboxOptionType } from 'antd/lib/checkbox/Group';
import dayjs from 'dayjs';
import { Filter as FilterIcon } from 'feather-icons-react';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { GlobalFilterTemplate } from 'components/GlobalFilter/GlobalFilterTemplate';
import { RadioOptions } from 'components/GlobalFilter/constants';
import { visibleSubcategories } from 'components/PerformanceTable/performanceTypes';
import { Chip, DateRange, Dropdown, Tooltip } from 'components/common';
import { Select } from 'components/common/select';

import {
  useAudiencesQuery,
  useConstantsQuery,
  useCreativesQuery,
  useInsertionOrdersQuery,
  useLineItemsQuery,
  useMediaTypesQuery,
  usePublishersQuery,
  useSubcategoriesQuery,
  useSplitsQuery,
  useCountriesQuery,
  useDeviceTypesQuery,
  useCitySearchQuery,
  useRegionSearchQuery,
} from 'modules/apiData/dataApiSlice';
import { Dimension, InsertionOrdersCampaign, Metric } from 'modules/misc/miscApiTypes';
import { useOrganization } from 'modules/organization/useOrganization';
import { DimensionFilter } from 'modules/performance/performanceApiTypes';

import { CAMPAIGN_DIMENSION } from '../../modules/performance/constants';
import {
  DSP_ADFORM,
  DSP_AMAZON,
  DSP_CRITEO,
  DSP_FREEWHEEL,
  DSP_VISTAR,
  DSP_XANDR,
} from '../../utils/constants';

const MAX_LENGHT = 35;
const truncateWithTooltip = (text: string) =>
  text.length > MAX_LENGHT ? (
    <Tooltip
      className="text-base-sm"
      title={<span className="text-base-sm font-normal">{text}</span>}
    >
      {text}
    </Tooltip>
  ) : (
    <span className="text-base-sm">{text}</span>
  );

interface AllOptions {
  [key: string]: Partial<Dimension | Metric>;
}
type Option = { label: ReactNode; value: string; disabled: boolean };

const convertDimensionsToTags = (
  allOptions: AllOptions,
  dimensions: DimensionFilter[],
  insertionOrders?: InsertionOrdersCampaign[],
) => {
  const getCampaignDisplayNames = (ids: string[]) => {
    if (!insertionOrders) return ids;

    return ids.map((id) => {
      const campaign = insertionOrders.find((io) => io.id === id);

      return campaign?.displayName || id;
    });
  };

  return dimensions.flatMap((dimension) => {
    const tags: {
      key: string;
      label: string;
      value: { dimension: DimensionFilter; radioOption: RadioOptions };
    }[] = [];

    const dimensionNameLabel = allOptions[dimension.dimension! || dimension.metric!].shownName;
    const isCampaignDimension = dimension.dimension === CAMPAIGN_DIMENSION;

    if (dimension?.include?.length > 0) {
      const displayValues = isCampaignDimension
        ? getCampaignDisplayNames(dimension.include)
        : dimension.include;
      const label = `${dimensionNameLabel} is '${displayValues.join(', ')}'`;

      tags.push({
        key: label,
        label,
        value: { dimension, radioOption: RadioOptions.INCLUDE },
      });
    }

    if (dimension?.exclude?.length > 0) {
      const displayValues = isCampaignDimension
        ? getCampaignDisplayNames(dimension.exclude)
        : dimension.exclude;
      const label = `${dimensionNameLabel} is not '${displayValues.join(', ')}'`;

      tags.push({
        key: label,
        label,
        value: { dimension, radioOption: RadioOptions.EXCLUDE },
      });
    }

    return tags;
  });
};

const createDeepCopy = (dimensions: DimensionFilter[]) =>
  dimensions.map((item) => ({
    ...item,
    ...(item.include && { include: [...item.include] }),
    ...(item.exclude && { exclude: [...item.exclude] }),
  }));

const createDimensionOption = (value: string, disabled: boolean = false) => ({
  label: truncateWithTooltip(value),
  value,
  disabled,
});

const createMenuOption = ([key, value]: [
  key: string,
  value: {
    shownName: string;
    relatesToXandrDashboard?: boolean;
    relatesToVistarDashboard?: boolean;
    relatesToCriteoDashboard?: boolean;
    relatesToAdformDashboard?: boolean;
    relatesToAmazonDashboard?: boolean;
    relatesToFreewheelDashboard?: boolean;
  },
]) => ({
  key,
  label: value?.shownName,
  relatesToXandrDashboard: value?.relatesToXandrDashboard,
  relatesToVistarDashboard: value?.relatesToVistarDashboard,
  relatesToCriteoDashboard: value?.relatesToCriteoDashboard,
  relatesToAdformDashboard: value?.relatesToAdformDashboard,
  relatesToAmazonDashboard: value?.relatesToAmazonDashboard,
  relatesToFreewheelDashboard: value?.relatesToFreewheelDashboard,
});

const disableOption = (
  option: Option,
  foundDim: DimensionFilter,
  action: 'exclude' | 'include',
) => ({
  ...option,
  disabled: option.disabled || foundDim?.[action]?.includes(option?.value),
});

export const GlobalFilter = ({
  dimensions,
  onChange,
  globalFilterTrigger,
  showDimensions = true,
  showSubcategories = true,
  maxFilterCount = undefined,
  onChangeForDatePicker,
}: {
  dimensions: DimensionFilter[];
  onChange: (newDimensions: DimensionFilter[]) => void;
  globalFilterTrigger?: string[] | null;
  showDimensions?: boolean;
  showSubcategories?: boolean;
  maxFilterCount?: number;
  onChangeForDatePicker?: ({ startDate, endDate, period }: DateRange) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenChipMenu, setIsOpenChipMenu] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState<string | null>(null);
  const [activeChip, setActiveChip] = useState<string | null>(null);
  const [currentRadioOption, setCurrentRadioOption] = useState<RadioOptions>(RadioOptions.INCLUDE);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const { currentAdvertiserId, dsp, isDspAdvertiserReady } = useOrganization();
  const skipQuery = {
    skip: !currentAdvertiserId || !dsp || !isDspAdvertiserReady,
  };

  const { data: constants } = useConstantsQuery(null);
  const { data: mediaTypes } = useMediaTypesQuery({ dsp });
  const { data: publishers } = usePublishersQuery({ dsp }, skipQuery);
  const { data: audiences } = useAudiencesQuery(
    { advertiserId: currentAdvertiserId, dsp },
    skipQuery,
  );
  const { data: creatives } = useCreativesQuery(
    { advertiserId: currentAdvertiserId, dsp },
    skipQuery,
  );
  const { data: insertionOrders } = useInsertionOrdersQuery(
    { advertiserId: currentAdvertiserId, dsp },
    skipQuery,
  );
  const { data: lineItems } = useLineItemsQuery(
    { advertiserId: currentAdvertiserId, dsp },
    skipQuery,
  );
  const { data: subcategories = {} } = useSubcategoriesQuery(currentAdvertiserId, skipQuery);
  const { data: splits } = useSplitsQuery(currentAdvertiserId, skipQuery);
  const { data: countries } = useCountriesQuery({ dsp }, skipQuery);
  const { data: devices } = useDeviceTypesQuery({ dsp }, skipQuery);

  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [regionSearchQuery, setRegionSearchQuery] = useState('');

  const { data: citySearchResults = [], isLoading: isCitySearchLoading } = useCitySearchQuery(
    { dsp: dsp!, search: citySearchQuery },
    { skip: citySearchQuery.length <= 2 || !dsp },
  );

  const { data: regionSearchResults = [], isLoading: isRegionSearchLoading } = useRegionSearchQuery(
    { dsp: dsp!, search: regionSearchQuery },
    { skip: regionSearchQuery.length <= 2 || !dsp },
  );

  const availableCategories = Object.keys(subcategories);

  const allDimensionOptions = (constants && constants.Dimensions) || {};
  const allSubcategoryOptions = (constants && constants.Metrics) || {};

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setSelectedDimension(null);
        setCurrentRadioOption(RadioOptions.INCLUDE);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setSelectedDimension]);

  useEffect(() => {
    if (globalFilterTrigger) {
      setSelectedDimension('CAMPAIGN');
      setIsOpen(true);
    }
  }, [globalFilterTrigger]);

  const foundDimension = useMemo(
    () => dimensions.find((el) => (el.dimension || el.metric) === selectedDimension),
    [dimensions, selectedDimension],
  );

  const handleOpenChipMenu = (isOpen: boolean) => {
    if (!isOpen) {
      setActiveChip(null);
      setSelectedDimension(null);
    }
    setIsOpenChipMenu(isOpen);
  };

  const handleTagDelete = (dimensionType: string, optionKey: RadioOptions) => {
    const deepCopyDimensions = createDeepCopy(dimensions);
    const foundDimension = deepCopyDimensions.find(
      (item) => (item.dimension || item.metric) === dimensionType,
    );

    if (foundDimension) {
      delete foundDimension[optionKey];
    }
    onChange(
      deepCopyDimensions.filter(
        (dimension) => !!dimension.exclude?.length || !!dimension.include?.length,
      ),
    );
  };

  const handleTagRender = (props: CustomTagProps) => {
    const { label, value } = props!;
    const isActive = activeChip === label;

    return (
      <Dropdown
        trigger={['click']}
        onOpenChange={handleOpenChipMenu}
        open={isOpenChipMenu && isActive}
        dropdownRender={() => (
          <GlobalFilterTemplate
            label={
              (allDimensionOptions[selectedDimension!] || allSubcategoryOptions[selectedDimension!])
                ?.shownName
            }
            checkboxOptions={disableDuplicatedOption(getDimensionOptions()) as CheckboxOptionType[]}
            selectedOptions={value?.dimension[value?.radioOption]}
            radioOption={value?.radioOption}
            handleDimensionRadioOption={setCurrentRadioOption}
            handleSubmitAction={(data) => addOrUpdateDimension(data, value?.radioOption)}
            isSearchBased={isSearchBasedDimension()}
            onSearch={getSearchHandler()}
            searchLoading={getSearchLoading()}
          />
        )}
      >
        <div>
          <Chip
            active={isActive}
            onClick={() => {
              setActiveChip(label as string);
              setSelectedDimension(value?.dimension.dimension || value?.dimension.metric);
              setIsOpenChipMenu(true);
            }}
            onClose={() =>
              handleTagDelete(
                value?.dimension.dimension || value?.dimension.metric,
                value?.radioOption,
              )
            }
          >
            {label}
          </Chip>
        </div>
      </Dropdown>
    );
  };

  const handleOptionSelect: MenuProps['onClick'] = ({ key }) => {
    setSelectedDimension(key);
  };

  // `selectable: false` and the menu-source guard in `handleDropdownOpenChange`
  // work together: items are navigation, not selections, and clicking one must
  // not auto-close the dropdown — we want to render the sub-filter inline.
  const menu: MenuProps = {
    items: [],
    onClick: handleOptionSelect,
    selectable: false,
  };

  const handleDropdownOpenChange = (menuOpen: boolean, info: { source: 'trigger' | 'menu' }) => {
    // Antd auto-closes the dropdown when a menu item is clicked; suppress that
    // close so the sub-filter template can render in place.
    if (!menuOpen && info.source === 'menu') return;
    setIsOpen(menuOpen);
  };

  if (showDimensions) {
    menu.items?.push({
      key: 'Dimensions',
      label: 'Dimensions',
      type: 'group',
      children: Object.entries(allDimensionOptions)
        .map(createMenuOption)
        .filter((d) => {
          if (dsp === DSP_XANDR) return d.relatesToXandrDashboard;
          if (dsp === DSP_VISTAR) return d.relatesToVistarDashboard;
          if (dsp === DSP_CRITEO) return d.relatesToCriteoDashboard;
          if (dsp === DSP_ADFORM) return d.relatesToAdformDashboard;
          if (dsp === DSP_AMAZON) return d.relatesToAmazonDashboard;
          if (dsp === DSP_FREEWHEEL) return d.relatesToFreewheelDashboard;

          return false;
        }),
    });
  }

  if (showSubcategories) {
    menu.items?.push({
      key: 'Subcategories',
      label: 'Subcategories',
      type: 'group',
      children: Object.entries(allSubcategoryOptions)
        .map(createMenuOption)
        .filter(({ key }) => availableCategories.includes(key)),
    });
  }

  const disableDuplicatedOption = (options: Option[] | undefined) =>
    foundDimension
      ? options?.map((option) => {
          if (currentRadioOption === RadioOptions.INCLUDE) {
            return disableOption(option, foundDimension, RadioOptions.EXCLUDE);
          } else if (currentRadioOption === RadioOptions.EXCLUDE) {
            return disableOption(option, foundDimension, RadioOptions.INCLUDE);
          }
        })
      : options;

  const getDimensionOptions = () => {
    switch (selectedDimension) {
      case 'PUBLISHER':
        return publishers?.map((value) => createDimensionOption(value));
      case 'CAMPAIGN':
        return insertionOrders?.map((io) => ({
          label: truncateWithTooltip(io.displayName),
          value: io.id,
          disabled: false,
        }));
      case 'CREATIVE':
        return creatives?.map((value) => createDimensionOption(value));
      case 'AUDIENCE':
        return audiences?.map((value) => createDimensionOption(value));
      case 'LINE_ITEM':
        return lineItems?.map((value) => createDimensionOption(value));
      case 'MEDIA_TYPE':
        return mediaTypes?.map(({ name }) => createDimensionOption(name));
      case 'SPLIT':
        return splits?.map((value) => createDimensionOption(value));
      case 'CITY':
        return citySearchResults?.map((value) => createDimensionOption(value));
      case 'STATE_REGION':
        return regionSearchResults?.map((value) => createDimensionOption(value));
      case 'COUNTRY':
        return countries?.map((value) => createDimensionOption(value));
      case 'MEDIA_OWNER':
        return publishers?.map((value) => createDimensionOption(value));
      case 'DEVICE_TYPE':
        return devices?.map((device) => createDimensionOption(device.name));
      default:
        return selectedDimension && availableCategories.includes(selectedDimension)
          ? subcategories[selectedDimension]?.map((value) => createDimensionOption(value))
          : [];
    }
  };

  const getSearchHandler = () => {
    switch (selectedDimension) {
      case 'CITY':
        return setCitySearchQuery;
      case 'STATE_REGION':
        return setRegionSearchQuery;
      default:
        return undefined;
    }
  };

  const getSearchLoading = () => {
    switch (selectedDimension) {
      case 'CITY':
        return isCitySearchLoading;
      case 'STATE_REGION':
        return isRegionSearchLoading;
      default:
        return false;
    }
  };

  const isSearchBasedDimension = () => {
    return selectedDimension === 'CITY' || selectedDimension === 'STATE_REGION';
  };

  const addOrUpdateDimension = (
    data: {
      radioOption: RadioOptions;
      checkboxOptions: CheckboxOptionType[];
    },
    previousRadioOption?: RadioOptions,
  ) => {
    const deepCopyDimensions = createDeepCopy(dimensions);

    const existingDimension = deepCopyDimensions.find(
      (item) => (item.dimension || item.metric) === selectedDimension,
    );

    if (existingDimension) {
      existingDimension[data.radioOption] = data.checkboxOptions as unknown as string[];

      if (previousRadioOption !== data.radioOption) {
        delete existingDimension[previousRadioOption!];
      }
      onChange(deepCopyDimensions);
      setIsOpenChipMenu(false);
      setActiveChip(null);

      adjustDateRangeToCampaignRange(deepCopyDimensions);
    } else {
      const newDimension = {
        ...(selectedDimension && visibleSubcategories.includes(selectedDimension)
          ? { metric: selectedDimension }
          : { dimension: selectedDimension }),
        ...{ [data.radioOption as string]: data.checkboxOptions },
      } as DimensionFilter;

      onChange([...deepCopyDimensions, newDimension]);

      adjustDateRangeToCampaignRange([newDimension]);
    }
    setIsOpen(false);
    setSelectedDimension(null);
  };

  const adjustDateRangeToCampaignRange = (dimensions: DimensionFilter[]) => {
    const campaignDimension = dimensions.find((d) => d.dimension === CAMPAIGN_DIMENSION);

    if (!onChangeForDatePicker || !insertionOrders?.length) return;

    const relevantCampaigns = campaignDimension?.include?.length
      ? insertionOrders.filter((io) => campaignDimension.include.includes(io.id))
      : insertionOrders;

    const today = dayjs();
    const fallbackStart = today.subtract(6, 'day'); // last 7 days inclusive
    const fallbackEnd = today;

    const startDates = relevantCampaigns
      .map((c) => (c.startDate ? dayjs(c.startDate) : null))
      .filter((d): d is dayjs.Dayjs => d !== null);
    const endDates = relevantCampaigns
      .map((c) => (c.endDate ? dayjs(c.endDate) : null))
      .filter((d): d is dayjs.Dayjs => d !== null);

    const earliestStartDate = startDates.length > 0 ? dayjs.min(startDates) : fallbackStart;
    const latestEndDate = endDates.length > 0 ? dayjs.max(endDates) : fallbackEnd;

    if (!latestEndDate) return;

    const adjustedEndDate = dayjs.min(today, latestEndDate);

    onChangeForDatePicker({
      period: 'CUSTOM',
      startDate: earliestStartDate,
      endDate: adjustedEndDate,
    });
  };

  const selectedOptions = foundDimension ? foundDimension[currentRadioOption] : [];
  const allOptions = { ...allDimensionOptions, ...allSubcategoryOptions };

  useEffect(() => {
    if (insertionOrders && insertionOrders.length > 0 && dimensions.length == 0) {
      let candidates = insertionOrders;

      // Step 1: Filter by latest end date
      const latestEndDate = dayjs.max(candidates.map((c) => dayjs(c.endDate)));

      candidates = candidates.filter((c) => dayjs(c.endDate).isSame(latestEndDate));

      if (candidates.length > 1) {
        // Step 2: Filter by latest start date
        const latestStartDate = dayjs.max(candidates.map((c) => dayjs(c.startDate)));

        candidates = candidates.filter((c) => dayjs(c.startDate).isSame(latestStartDate));
      }

      if (candidates.length > 1) {
        // Step 3: Sort alphabetically by displayName and pick the first
        candidates = candidates.sort((a, b) => a.displayName.localeCompare(b.displayName));
      }

      const selectedCampaign = candidates.length == 0 ? insertionOrders[0] : candidates[0];

      const campaignDimensionFilter: DimensionFilter = {
        dimension: 'CAMPAIGN',
        include: [selectedCampaign?.id],
        exclude: [],
      };

      onChange([...dimensions, campaignDimensionFilter]);
      setSelectedDimension('CAMPAIGN');
      setCurrentRadioOption(RadioOptions.INCLUDE);
      if (onChangeForDatePicker && selectedCampaign) {
        const today = dayjs();
        const fallbackStart = today.subtract(6, 'day');
        const fallbackEnd = today;

        const campaignStartDate = selectedCampaign?.startDate
          ? dayjs(selectedCampaign.startDate)
          : fallbackStart;
        const campaignEndDate = selectedCampaign?.endDate
          ? dayjs(selectedCampaign.endDate)
          : fallbackEnd;

        const maxEndDate = campaignEndDate.add(30, 'day');
        const adjustedEndDate = dayjs.min(today, maxEndDate);

        onChangeForDatePicker({
          period: 'CUSTOM',
          startDate: campaignStartDate,
          endDate: adjustedEndDate,
        });
      }
    } else {
      if (onChangeForDatePicker) {
        onChangeForDatePicker({
          period: 'LAST_7_DAYS',
          startDate: dayjs().subtract(7, 'days'),
          endDate: dayjs(),
        });
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [insertionOrders]);

  return (
    <Flex className="flex-1">
      <Dropdown
        trigger={['click']}
        onOpenChange={handleDropdownOpenChange}
        open={isOpen}
        menu={menu}
        dropdownRender={(menu) => (
          <div>
            {selectedDimension ? (
              <div ref={filterMenuRef}>
                <GlobalFilterTemplate
                  label={
                    (
                      allDimensionOptions[selectedDimension] ||
                      allSubcategoryOptions[selectedDimension]
                    )?.shownName
                  }
                  checkboxOptions={
                    disableDuplicatedOption(getDimensionOptions()) as CheckboxOptionType[]
                  }
                  selectedOptions={selectedOptions as unknown as CheckboxOptionType[]}
                  radioOption={currentRadioOption}
                  handleDimensionRadioOption={setCurrentRadioOption}
                  handleSubmitAction={addOrUpdateDimension}
                  isSearchBased={isSearchBasedDimension()}
                  onSearch={getSearchHandler()}
                  searchLoading={getSearchLoading()}
                />
              </div>
            ) : (
              <div className="filter-dropdown-options">{menu}</div>
            )}
          </div>
        )}
      >
        <Flex gap="small" align="center" className={`filter-button ${isOpen ? 'active' : ''}`}>
          <FilterIcon className="h-5 w-5" /> Filter
        </Flex>
      </Dropdown>
      <Select
        className="filter-select"
        allowClear={{
          clearIcon: (
            <div className="filter-clear cursor-pointer text-base-sm font-medium leading-tight underline">
              Clear all
            </div>
          ),
        }}
        open={false}
        mode="multiple"
        maxTagCount={maxFilterCount}
        value={convertDimensionsToTags(allOptions, dimensions, insertionOrders)}
        tagRender={handleTagRender}
        onClear={() => onChange([])}
      />
    </Flex>
  );
};
